import React from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, ComposedChart
} from 'recharts';
import { formatBytes, MetricBar } from './DashboardShared';
import Panel from '../../components/design/Panel';

export default function DashboardPerformance({
  enhancedLoading, forecastData, diskIO, diskHistory, logVolume, queueDepth, uptimeSummary, dbSizes, forecast, kafkaLag,
}) {
  const fd = forecastData || {};
  const cpuForecast = fd.cpu || {};
  const memForecast = fd.memory || {};
  const diskForecast = fd.disk || {};
  const fDays = 7;

  const makeForecastData = (history = [], forecastArr = []) => {
    const safeMap = (arr, fn) => (Array.isArray(arr) ? arr : []).map(fn);
    const fmtDate = (d) => {
      if (!d) return '';
      if (typeof d === 'number') return new Date(d * 1000).toISOString().slice(0, 16);
      return String(d).slice(0, 16);
    };
    const hist = safeMap(history, h => ({ date: fmtDate(h.date), value: Number(h.value) || 0, type: 'history' }));
    const pred = safeMap(forecastArr, f => ({ date: fmtDate(f.date), value: Number(f.value) || 0, lower: Number(f.lower) || 0, upper: Number(f.upper) || 0, type: 'forecast' }));
    return [...hist.slice(-48), ...pred];
  };

  return (
    <div>
      <div className="grid grid-3 section-gap">
        {cpuForecast.history?.length > 0 && (
          <Panel title={`CPU Forecast (30d + ${fDays}d)`}>
            <div className="chart-box sm">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={makeForecastData(cpuForecast.history, cpuForecast.forecast)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="date" tick={false} />
                  <YAxis tick={{ fontSize: 10 }} stroke="var(--text-3)" domain={[0, 100]} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} dot={false} />
                  <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} strokeDasharray="5 5" dot={false}
                    data={makeForecastData(cpuForecast.history, cpuForecast.forecast).filter(d => d.type === 'forecast')} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        )}
        {memForecast.history?.length > 0 && (
          <Panel title={`Memory Forecast (30d + ${fDays}d)`}>
            <div className="chart-box sm">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={makeForecastData(memForecast.history, memForecast.forecast)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="date" tick={false} />
                  <YAxis tick={{ fontSize: 10 }} stroke="var(--text-3)" domain={[0, 100]} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="value" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.15} dot={false} />
                  <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" dot={false}
                    data={makeForecastData(memForecast.history, memForecast.forecast).filter(d => d.type === 'forecast')} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        )}
        {diskForecast.history?.length > 0 && (
          <Panel title={`Disk Forecast (30d + ${fDays}d)`}>
            <div className="chart-box sm">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={makeForecastData(diskForecast.history, diskForecast.forecast)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="date" tick={false} />
                  <YAxis tick={{ fontSize: 10 }} stroke="var(--text-3)" domain={[0, 100]} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="value" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.15} dot={false} />
                  <Line type="monotone" dataKey="value" stroke="#06b6d4" strokeWidth={2} strokeDasharray="5 5" dot={false}
                    data={makeForecastData(diskForecast.history, diskForecast.forecast).filter(d => d.type === 'forecast')} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        )}
      </div>

      <div className="grid grid-2 section-gap">
        <Panel title={`Disk Read (${formatBytes(diskIO.read_bps)}/s)`}>
          <div className="chart-box sm">
            {diskHistory.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={diskHistory}>
                  <defs><linearGradient id="readGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} /><stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="time" tick={{ fontSize: 8 }} stroke="var(--text-3)" />
                  <YAxis tick={{ fontSize: 8 }} stroke="var(--text-3)" tickFormatter={v => formatBytes(v)} />
                  <Tooltip formatter={v => formatBytes(v) + '/s'} />
                  <Area type="monotone" dataKey="read" stroke="#8b5cf6" fill="url(#readGrad)" strokeWidth={2} dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="empty">Collecting...</div>}
          </div>
        </Panel>
        <Panel title={`Disk Write (${formatBytes(diskIO.write_bps)}/s)`}>
          <div className="chart-box sm">
            {diskHistory.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={diskHistory}>
                  <defs><linearGradient id="writeGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} /><stop offset="95%" stopColor="#06b6d4" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="time" tick={{ fontSize: 8 }} stroke="var(--text-3)" />
                  <YAxis tick={{ fontSize: 8 }} stroke="var(--text-3)" tickFormatter={v => formatBytes(v)} />
                  <Tooltip formatter={v => formatBytes(v) + '/s'} />
                  <Area type="monotone" dataKey="write" stroke="#06b6d4" fill="url(#writeGrad)" strokeWidth={2} dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="empty">Collecting...</div>}
          </div>
        </Panel>
      </div>

      <div className="grid grid-2 section-gap">
        <Panel title="Log Volume (24h)">
          <div className="chart-box sm">
            {enhancedLoading ? <div className="empty">Loading...</div> : logVolume.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={logVolume}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="hour" tick={{ fontSize: 8 }} stroke="var(--text-3)" />
                  <YAxis tick={{ fontSize: 8 }} stroke="var(--text-3)" />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="empty">No data</div>}
          </div>
        </Panel>
        <Panel title="Queue Depth (24h)">
          <div className="chart-box sm">
            {queueDepth.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={queueDepth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="hour" tick={{ fontSize: 8 }} stroke="var(--text-3)" />
                  <YAxis tick={{ fontSize: 8 }} stroke="var(--text-3)" />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="empty">No data</div>}
          </div>
        </Panel>
      </div>

      <div className="grid main-3 section-gap">
        <Panel title="Server Uptime">
          <div className="space-y-1">
            {enhancedLoading ? (
              <div className="empty">Loading...</div>
            ) : uptimeSummary.length === 0 ? (
              <div className="empty">No data</div>
            ) : (
              uptimeSummary.slice(0, 6).map(s => (
                <div key={s.name} className="list-row" style={{ padding: '5px 0' }}>
                  <span style={{ color: 'var(--text-2)', fontSize: 12 }}>{s.name}</span>
                  <b className="health-score" style={{ marginLeft: 'auto', color: s.uptime_percent >= 99 ? 'var(--success)' : s.uptime_percent >= 95 ? 'var(--warning)' : 'var(--danger)' }}>{s.uptime_percent.toFixed(1)}%</b>
                </div>
              ))
            )}
          </div>
        </Panel>
        <Panel title="Database Sizes">
          <div className="space-y-2">
            <div><div className="flex justify-between text-[11px]"><span style={{ color: 'var(--text-3)' }}>PostgreSQL</span><span className="mono" style={{ color: 'var(--text-1)', fontWeight: 700 }}>{dbSizes.postgresql_mb || 0} MB</span></div><MetricBar value={Math.min((dbSizes.postgresql_mb || 0) / 100, 100)} color="bg-indigo-500" /></div>
            <div><div className="flex justify-between text-[11px]"><span style={{ color: 'var(--text-3)' }}>ClickHouse</span><span className="mono" style={{ color: 'var(--text-1)', fontWeight: 700 }}>{dbSizes.clickhouse_mb || 0} MB</span></div><MetricBar value={Math.min((dbSizes.clickhouse_mb || 0) / 500, 100)} color="bg-cyan-500" /></div>
          </div>
        </Panel>
        <Panel title="Resource Forecast">
          <div className="space-y-1.5">
            <div className="list-row" style={{ padding: '5px 0' }}><span style={{ color: 'var(--text-3)' }}>Disk Full</span><b className="health-score" style={{ marginLeft: 'auto', color: forecast.disk_full_days === -1 ? 'var(--success)' : !forecast.disk_full_days ? 'var(--text-3)' : forecast.disk_full_days < 30 ? 'var(--danger)' : 'var(--success)' }}>{forecast.disk_full_days === -1 ? 'Shrinking' : forecast.disk_full_days || 'N/A'}{forecast.disk_full_days > 0 ? 'd' : ''}</b></div>
            <div className="list-row" style={{ padding: '5px 0' }}><span style={{ color: 'var(--text-3)' }}>RAM Full</span><b className="health-score" style={{ marginLeft: 'auto', color: forecast.ram_full_days === -1 ? 'var(--success)' : !forecast.ram_full_days ? 'var(--text-3)' : forecast.ram_full_days < 30 ? 'var(--danger)' : 'var(--success)' }}>{forecast.ram_full_days === -1 ? 'Decreasing' : forecast.ram_full_days || 'N/A'}{forecast.ram_full_days > 0 ? 'd' : ''}</b></div>
            <div className="list-row" style={{ padding: '5px 0' }}><span style={{ color: 'var(--text-3)' }}>Kafka Lag</span><b className="health-score" style={{ marginLeft: 'auto', color: kafkaLag > 1000 ? 'var(--danger)' : 'var(--success)' }}>{kafkaLag}</b></div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
